import { useMemo } from "react";
import { Check } from "lucide-react";

/**
 * Step 1 of the assessment: pick your field of work.
 *
 * Users pick a primary field (required) and an optional secondary field —
 * we use the union of skill chips across the selected fields in Step 2. Plus
 * Step 2 still has an escape-hatch search over the *other* fields.
 */
export const FieldPickerStep = ({
  fields,
  primaryFieldId,
  secondaryFieldId,
  onPrimaryChange,
  onSecondaryChange,
}) => {
  // Memoised: the "_other" bucket is only interesting as a search escape
  // hatch in Step 2 — it should never show up as a pickable field card.
  const visibleFields = useMemo(
    () => fields.filter((f) => f.id !== "_other"),
    [fields],
  );

  return (
    <div>
      <h2
        data-testid="field-step-title"
        className="font-display text-2xl sm:text-3xl font-bold text-slate-900"
      >
        What kind of work do you do?
      </h2>
      <p className="mt-3 text-sm text-slate-600 max-w-2xl">
        Pick the field you spend most of your time in. We'll show you only the
        skills that matter for the 54 Australian IT roles in that space —
        nothing more. You can add a secondary field if you straddle two.
      </p>

      <div
        data-testid="field-cards"
        className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        {visibleFields
          .map((field) => {
            const isPrimary = field.id === primaryFieldId;
            const isSecondary = field.id === secondaryFieldId;
            const isSelected = isPrimary || isSecondary;
            const label = isPrimary
              ? "Primary"
              : isSecondary
                ? "Secondary"
                : "";

            const handleClick = () => {
              if (isPrimary) {
                // Demote primary → clear it. Bring secondary up if any.
                onPrimaryChange(secondaryFieldId || null);
                onSecondaryChange(null);
                return;
              }
              if (isSecondary) {
                onSecondaryChange(null);
                return;
              }
              if (!primaryFieldId) {
                onPrimaryChange(field.id);
              } else if (!secondaryFieldId) {
                onSecondaryChange(field.id);
              } else {
                // Already have both → swap secondary for new pick.
                onSecondaryChange(field.id);
              }
            };

            return (
              <button
                key={field.id}
                type="button"
                data-testid={`field-card-${field.id}`}
                aria-pressed={isSelected}
                onClick={handleClick}
                className={`group relative text-left rounded-xl border p-4 transition-all
                  ${isSelected
                    ? "border-amber-500 bg-amber-50/60 ring-2 ring-amber-200"
                    : "border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50"
                  }`}
              >
                {isSelected && (
                  <span
                    data-testid={`field-card-${field.id}-badge`}
                    className={`absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider
                      ${isPrimary
                        ? "bg-amber-500 text-white"
                        : "bg-slate-700 text-white"}`}
                  >
                    <Check size={10} strokeWidth={3} />
                    {label}
                  </span>
                )}
                <h3 className="font-display text-base font-bold text-slate-900 pr-16">
                  {field.label}
                </h3>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600 pr-2">
                  {field.blurb}
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  {field.role_count} {field.role_count === 1 ? "role" : "roles"}
                  {" · "}
                  {field.technical_skills.length + field.soft_skills.length}{" "}
                  skills
                </p>
              </button>
            );
          })}
      </div>

      {primaryFieldId && (
        <div
          data-testid="field-step-hint"
          className="mt-6 rounded-md border border-slate-200 bg-white p-3 text-[12px] text-slate-600"
        >
          {secondaryFieldId
            ? "Got it — we’ll show you skills from both fields next, plus a search to pull in anything from the other areas."
            : "Tap a second card to add a secondary field, or hit Continue when you’re ready."}
        </div>
      )}
    </div>
  );
};

export default FieldPickerStep;
